// src/components/DataDictPanel.tsx (2026-07-22 新設・データ辞書βタブ)
// 配信 duckdb の中身を「テーブル1つずつ」確認する社内向けページ。
//   左: DB別テーブル一覧 (行数・PII判定バッジ)
//   右: 選択テーブルの [概要 / 列定義 / リレーション / 実データプレビュー]
//
// ★実データプレビューは server 側で PII 列 (ptid_anon=可逆擬似匿名化患者ID・医師名=スタッフ) を
//   マスク済み。値は '●●●(マスク)' で返る。フロントで raw PII を扱うことはない。
//
// api.ts (所有者別) には手を入れず、この panel 内で self-contained に fetch する。
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { GP_MODE_HEADER } from "../lib/gpMode";

// ── 型 (server/dict.ts のレスポンスに対応) ──
interface DbInfo { key: string; catalog: string; label: string; desc: string }
interface TableRow {
  db: string; table: string; label: string | null;
  rows: number; cols: number; desc: string | null;
  pii: "clean" | "caution" | "staff" | "unknown"; curated: boolean;
}
interface TablesResp { dbs: DbInfo[]; tables: TableRow[]; coverage: { total: number; curated: number } }
interface ColDef {
  name: string; type: string; nullRate: number | null; distinct: number | null;
  desc: string | null; pii: "patient_id" | "staff" | null;
}
interface SchemaResp {
  db: string; table: string; label: string | null; desc: string | null;
  source: string | null; grain: string | null;
  piiClass: string; piiNote: string | null;
  totalRows: number; statsComputed: boolean; columns: ColDef[];
}
interface SampleResp {
  db: string; table: string; columns: string[];
  rows: Record<string, unknown>[]; limit: number; offset: number;
  totalRows: number; masked: string[]; sqlMs: number;
}
interface Relation { from: string; to: string; kind: string; note?: string }
interface RelationsResp { relations: Relation[] }

async function dictFetch<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include", headers: { ...GP_MODE_HEADER } });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

// PII バッジ
function PiiBadge({ pii }: { pii: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    clean:   { label: "clean",  cls: "bg-emerald-100 text-emerald-700" },
    caution: { label: "注意",   cls: "bg-red-100 text-red-700" },
    staff:   { label: "職員情報", cls: "bg-amber-100 text-amber-800" },
    unknown: { label: "未判定", cls: "bg-slate-100 text-slate-500" },
  };
  const m = map[pii] ?? map.unknown;
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

const fmtInt = (n: number) => n.toLocaleString("ja-JP");
const fmtPct = (r: number | null) => (r == null ? "—" : `${(r * 100).toFixed(r >= 0.999 || r === 0 ? 0 : 1)}%`);
// セル値を安全に文字列化 (null → —, object → JSON, それ以外 → String)
function cellStr(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

type SubTab = "概要" | "列定義" | "リレーション" | "実データ";

export function DataDictPanel() {
  const [sel, setSel] = useState<{ db: string; table: string } | null>(null);
  const [sub, setSub] = useState<SubTab>("概要");
  const [offset, setOffset] = useState(0);
  const PAGE = 50;

  const tablesQ = useQuery({ queryKey: ["dict_tables"], queryFn: () => dictFetch<TablesResp>("/api/dict/tables"), staleTime: 300_000 });
  const relQ = useQuery({ queryKey: ["dict_relations"], queryFn: () => dictFetch<RelationsResp>("/api/dict/relations"), staleTime: Infinity });
  const schemaQ = useQuery({
    queryKey: ["dict_schema", sel?.db, sel?.table],
    queryFn: () => dictFetch<SchemaResp>(`/api/dict/schema?db=${encodeURIComponent(sel!.db)}&table=${encodeURIComponent(sel!.table)}`),
    enabled: !!sel,
    staleTime: 300_000,
  });
  const sampleQ = useQuery({
    queryKey: ["dict_sample", sel?.db, sel?.table, offset],
    queryFn: () => dictFetch<SampleResp>(`/api/dict/sample?db=${encodeURIComponent(sel!.db)}&table=${encodeURIComponent(sel!.table)}&limit=${PAGE}&offset=${offset}`),
    enabled: !!sel && sub === "実データ",
    placeholderData: (prev) => prev,
  });

  const pick = (db: string, table: string) => { setSel({ db, table }); setSub("概要"); setOffset(0); };

  const tables = tablesQ.data?.tables ?? [];
  const dbs = tablesQ.data?.dbs ?? [];
  // このテーブルに関わるリレーション (from/to どちらか)
  const relsForSel = (relQ.data?.relations ?? []).filter((r) => {
    if (!sel) return false;
    const pfx = `${sel.db}.${sel.table}.`;
    return r.from.startsWith(pfx) || r.to.startsWith(pfx);
  });

  return (
    <div className="space-y-3">
      <section className="card p-3">
        <h2 className="text-sm font-bold mb-1">🗂️ データ辞書（内部DBのテーブル定義・リレーション・実データ確認）</h2>
        <p className="text-[11px] text-slate-500">
          配信 duckdb（{dbs.map((d) => d.key).join(" / ")}）の全テーブルを1つずつ確認できます。
          実データプレビューは<span className="font-medium text-red-600">個人情報列（患者ID・医師氏名）をマスク</span>しています。
          {tablesQ.data && <span className="ml-2 text-slate-400">辞書カバレッジ {tablesQ.data.coverage.curated}/{tablesQ.data.coverage.total}</span>}
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-3">
        {/* ── 左: DB別テーブル一覧 ── */}
        <section className="card p-2 h-fit lg:sticky lg:top-16 max-h-[80vh] overflow-y-auto">
          {tablesQ.isLoading && <div className="text-xs text-slate-400 p-2">読み込み中…</div>}
          {tablesQ.error && <div className="text-xs text-red-600 p-2">{(tablesQ.error as Error).message}</div>}
          {dbs.map((db) => {
            const dbTables = tables.filter((t) => t.db === db.key);
            return (
              <div key={db.key} className="mb-2">
                <div className="px-1.5 py-1 text-[11px] font-bold text-slate-700 bg-slate-100 rounded sticky top-0" title={db.desc}>
                  {db.label} <span className="font-normal text-slate-400">({dbTables.length})</span>
                </div>
                <ul>
                  {dbTables.map((t) => {
                    const active = sel?.db === t.db && sel?.table === t.table;
                    return (
                      <li key={t.table}>
                        <button
                          onClick={() => pick(t.db, t.table)}
                          className={`w-full text-left px-1.5 py-1 rounded flex items-center justify-between gap-1 ${active ? "bg-emerald-50 outline outline-1 outline-emerald-300" : "hover:bg-slate-50"}`}
                          title={t.desc ?? undefined}
                        >
                          <span className="min-w-0">
                            <span className={`text-[12px] font-mono truncate block ${active ? "text-emerald-800 font-semibold" : "text-slate-700"}`}>{t.table}</span>
                            {t.label && <span className="text-[10px] text-slate-400 truncate block">{t.label}</span>}
                          </span>
                          <span className="flex items-center gap-1 shrink-0">
                            {t.pii !== "clean" && <PiiBadge pii={t.pii} />}
                            <span className="text-[10px] text-slate-400 tabular-nums">{fmtInt(t.rows)}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </section>

        {/* ── 右: 選択テーブルの詳細 ── */}
        <section className="min-w-0">
          {!sel && <div className="card p-6 text-sm text-slate-400">左のテーブルを選択してください。</div>}
          {sel && (
            <div className="card p-3">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <h3 className="text-base font-mono font-semibold">{sel.table}</h3>
                {schemaQ.data && <PiiBadge pii={schemaQ.data.piiClass} />}
                {schemaQ.data?.label && <span className="text-sm text-slate-500">{schemaQ.data.label}</span>}
                <span className="text-[11px] text-slate-400 ml-auto">{sel.db}</span>
              </div>

              {/* サブタブ */}
              <div className="flex gap-1 border-b border-slate-200 mb-3">
                {(["概要", "列定義", "リレーション", "実データ"] as SubTab[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setSub(t)}
                    className={`px-3 py-1.5 text-xs border-b-2 -mb-px ${sub === t ? "border-brand text-brand font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                  >
                    {t}{t === "リレーション" && relsForSel.length > 0 && <span className="ml-1 text-[10px] text-slate-400">({relsForSel.length})</span>}
                  </button>
                ))}
              </div>

              {schemaQ.isLoading && <div className="text-xs text-slate-400">読み込み中…</div>}
              {schemaQ.error && <div className="text-xs text-red-600">{(schemaQ.error as Error).message}</div>}

              {/* 概要 */}
              {sub === "概要" && schemaQ.data && (
                <div className="space-y-2 text-sm">
                  {schemaQ.data.piiClass !== "clean" && schemaQ.data.piiNote && (
                    <div className="rounded border border-red-200 bg-red-50 p-2 text-[12px] text-red-800">
                      <span className="font-bold">PII注意: </span>{schemaQ.data.piiNote}
                    </div>
                  )}
                  <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[13px]">
                    <dt className="text-slate-400">説明</dt><dd>{schemaQ.data.desc ?? "（辞書未定義）"}</dd>
                    <dt className="text-slate-400">粒度(1行)</dt><dd>{schemaQ.data.grain ?? "—"}</dd>
                    <dt className="text-slate-400">生成元</dt><dd className="font-mono text-[12px]">{schemaQ.data.source ?? "—"}</dd>
                    <dt className="text-slate-400">行数</dt><dd className="tabular-nums">{fmtInt(schemaQ.data.totalRows)}</dd>
                    <dt className="text-slate-400">列数</dt><dd className="tabular-nums">{schemaQ.data.columns.length}</dd>
                    <dt className="text-slate-400">PII判定</dt><dd><PiiBadge pii={schemaQ.data.piiClass} /></dd>
                  </dl>
                </div>
              )}

              {/* 列定義 */}
              {sub === "列定義" && schemaQ.data && (
                <div className="overflow-x-auto">
                  {!schemaQ.data.statsComputed && <p className="text-[11px] text-amber-600 mb-1">※大規模表のため NULL率/distinct は省略</p>}
                  <table className="text-[12px] border-collapse w-full">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left px-2 py-1 border-b border-slate-200">#</th>
                        <th className="text-left px-2 py-1 border-b border-slate-200">列名</th>
                        <th className="text-left px-2 py-1 border-b border-slate-200">型</th>
                        <th className="text-right px-2 py-1 border-b border-slate-200">NULL率</th>
                        <th className="text-right px-2 py-1 border-b border-slate-200">distinct</th>
                        <th className="text-left px-2 py-1 border-b border-slate-200">定義</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schemaQ.data.columns.map((col, i) => (
                        <tr key={col.name} className={col.pii ? "bg-red-50/50" : "hover:bg-slate-50"}>
                          <td className="px-2 py-1 border-b border-slate-100 text-slate-400 tabular-nums">{i + 1}</td>
                          <td className="px-2 py-1 border-b border-slate-100 font-mono whitespace-nowrap">
                            {col.name}
                            {col.pii && <span className="ml-1 px-1 rounded bg-red-100 text-red-700 text-[9px]">{col.pii === "patient_id" ? "患者ID" : "職員"}</span>}
                          </td>
                          <td className="px-2 py-1 border-b border-slate-100 font-mono text-slate-500 whitespace-nowrap">{col.type.length > 40 ? col.type.slice(0, 40) + "…" : col.type}</td>
                          <td className="px-2 py-1 border-b border-slate-100 text-right tabular-nums text-slate-500">{fmtPct(col.nullRate)}</td>
                          <td className="px-2 py-1 border-b border-slate-100 text-right tabular-nums text-slate-500">{col.distinct == null ? "—" : fmtInt(col.distinct)}</td>
                          <td className="px-2 py-1 border-b border-slate-100 text-slate-600">{col.desc ?? <span className="text-slate-300">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* リレーション */}
              {sub === "リレーション" && (
                <div className="text-[12px]">
                  {relsForSel.length === 0 && <p className="text-slate-400">このテーブルに紐づく curated リレーションはありません。</p>}
                  {relsForSel.length > 0 && (
                    <table className="border-collapse w-full">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left px-2 py-1 border-b">from</th>
                          <th className="text-center px-2 py-1 border-b">関係</th>
                          <th className="text-left px-2 py-1 border-b">to</th>
                          <th className="text-left px-2 py-1 border-b">備考</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relsForSel.map((r, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="px-2 py-1 border-b border-slate-100 font-mono">{r.from}</td>
                            <td className="px-2 py-1 border-b border-slate-100 text-center text-slate-500 whitespace-nowrap">{r.kind}</td>
                            <td className="px-2 py-1 border-b border-slate-100 font-mono">{r.to}</td>
                            <td className="px-2 py-1 border-b border-slate-100 text-slate-500">{r.note ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <p className="text-[10px] text-slate-400 mt-2">※ duckdb は FK 制約を持たないため、build script の読解＋列名照合で人手 curate したものです。</p>
                </div>
              )}

              {/* 実データプレビュー */}
              {sub === "実データ" && (
                <div>
                  <div className="flex items-center gap-2 mb-2 text-[11px] text-slate-500 flex-wrap">
                    {sampleQ.data && (
                      <>
                        <span>{fmtInt(sampleQ.data.totalRows)} 行中 {offset + 1}–{Math.min(offset + PAGE, sampleQ.data.totalRows)} 行</span>
                        {sampleQ.data.masked.length > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700">マスク列: {sampleQ.data.masked.join(", ")}</span>
                        )}
                        <span className="text-slate-400">SQL {sampleQ.data.sqlMs} ms</span>
                        <span className="ml-auto flex gap-1">
                          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="px-2 py-0.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">← 前</button>
                          <button disabled={offset + PAGE >= sampleQ.data.totalRows} onClick={() => setOffset(offset + PAGE)} className="px-2 py-0.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">次 →</button>
                        </span>
                      </>
                    )}
                    {sampleQ.isFetching && <span className="text-slate-400">更新中…</span>}
                  </div>
                  {sampleQ.error && <div className="text-xs text-red-600">{(sampleQ.error as Error).message}</div>}
                  {sampleQ.data && (
                    <div className="overflow-x-auto border border-slate-200 rounded max-h-[65vh]">
                      <table className="text-[11px] border-collapse">
                        <thead>
                          <tr className="bg-slate-100 sticky top-0">
                            <th className="px-2 py-1 border-b border-r border-slate-200 text-slate-400 sticky left-0 bg-slate-100">#</th>
                            {sampleQ.data.columns.map((cnm) => {
                              const masked = sampleQ.data!.masked.includes(cnm);
                              return (
                                <th key={cnm} className={`px-2 py-1 border-b border-slate-200 text-left font-mono whitespace-nowrap ${masked ? "text-red-600" : "text-slate-700"}`}>
                                  {cnm}{masked && " 🔒"}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {sampleQ.data.rows.map((row, ri) => (
                            <tr key={ri} className="hover:bg-slate-50">
                              <td className="px-2 py-1 border-b border-r border-slate-100 text-slate-400 tabular-nums sticky left-0 bg-white">{offset + ri + 1}</td>
                              {sampleQ.data!.columns.map((cnm) => (
                                <td key={cnm} className="px-2 py-1 border-b border-slate-100 whitespace-nowrap max-w-[280px] truncate" title={cellStr(row[cnm])}>
                                  {cellStr(row[cnm])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
